import { debounce } from 'lodash';

import WebSocketConnection from './websocket-connection';

type TopicSubscription = {
  topic: string;
  views: Set<string>;
};

export default class TopicManager {
  private webSocketConnection: WebSocketConnection;

  private subscriptions: Map<string, TopicSubscription> = new Map();

  // Track what we've last sent to the server
  private lastSentSubscriptions: Set<string> = new Set();

  private readonly debounceDelay: number;

  private debouncedSendChanges: (() => void) & { flush?: () => void };

  private connectedHandler: (() => void) | null = null;

  constructor(
    webSocketConnection: WebSocketConnection,
    debounceFn: typeof debounce = debounce,
    debounceDelay = 50,
  ) {
    this.webSocketConnection = webSocketConnection;
    this.debounceDelay = debounceDelay;

    // Create debounced function for sending changes using the provided debounce implementation
    this.debouncedSendChanges = debounceFn(() => {
      this.sendStateChanges();
    }, this.debounceDelay);

    // Store the connected handler so we can remove it later
    this.connectedHandler = () => {
      const activeSubscriptions = Array.from(this.subscriptions.keys());
      console.info(
        `[TOPIC MANAGER] websocket connected, subscribing on topics: ${activeSubscriptions} topics`,
      );
      this.sendSubscriptions(activeSubscriptions, true);
      // Update last sent state
      this.lastSentSubscriptions = new Set(activeSubscriptions);
    };

    this.webSocketConnection.addEventListener(
      'connected',
      this.connectedHandler,
    );
  }

  updateView(view: string, topics: string[]) {
    this.clearView(view);
    topics.filter(Boolean).forEach((topic) => {
      const subscription = this.subscriptions.get(topic);
      if (subscription) {
        subscription.views.add(view);
      } else {
        const newSubscription = { topic, views: new Set([view]) };
        this.subscriptions.set(topic, newSubscription);
      }
    });

    this.debouncedSendChanges();
  }

  /**
   * Remove all topic subscriptions associated with a specific view
   * @param view The view identifier to unsubscribe from all topics
   */
  clearView(view: string) {
    const topicsToUnsubscribe: string[] = [];

    // Find all topics that have this view subscribed
    this.subscriptions.forEach((subscription, topic) => {
      if (subscription.views.has(view)) {
        subscription.views.delete(view);
        if (subscription.views.size === 0) {
          this.subscriptions.delete(topic);
          topicsToUnsubscribe.push(topic);
        }
      }
    });

    // If any topics were fully unsubscribed, trigger state changes
    if (topicsToUnsubscribe.length > 0) {
      this.debouncedSendChanges();
    }
  }

  /**
   * Clean up the TopicManager by removing event listeners and canceling debounced functions
   */
  cleanup() {
    // Remove the connected event listener
    if (this.connectedHandler) {
      this.webSocketConnection.removeEventListener(
        'connected',
        this.connectedHandler,
      );
      this.connectedHandler = null;
    }

    // Cancel any pending debounced function calls
    if (this.debouncedSendChanges?.flush) {
      this.debouncedSendChanges.flush();
    }
  }

  private sendStateChanges() {
    const currentSubscriptions = Array.from(this.subscriptions.keys());
    const lastSentArray = Array.from(this.lastSentSubscriptions);

    // Find topics that need to be subscribed (in current but not in last sent)
    const topicsToSubscribe = currentSubscriptions.filter(
      (topic) => !this.lastSentSubscriptions.has(topic),
    );

    // Find topics that need to be unsubscribed (in last sent but not in current)
    const topicsToUnsubscribe = lastSentArray.filter(
      (topic) => !this.subscriptions.has(topic),
    );
    if (topicsToUnsubscribe.length > 0 || topicsToSubscribe.length > 0) {
      console.info(
        `[TOPIC MANAGER] sending state changes: unsubscribe: ${topicsToUnsubscribe}, subscribe: ${topicsToSubscribe}`,
      );
    }

    // Send unsubscribe first, then subscribe
    if (topicsToUnsubscribe.length > 0) {
      this.sendUnsubscribe(topicsToUnsubscribe);
    }

    if (topicsToSubscribe.length > 0) {
      this.sendSubscriptions(topicsToSubscribe);
    }

    // Update last sent state to match current state
    this.lastSentSubscriptions = new Set(currentSubscriptions);
  }

  private sendSubscriptions(topics: string[], newConnection = false) {
    if (topics.length > 0) {
      this.webSocketConnection.send({
        action: 'subscribe',
        topics,
        timestamp: this.webSocketConnection.lastTimestamp,
        newConnection,
      });
    }
  }

  private sendUnsubscribe(topics: string[]) {
    if (topics.length > 0) {
      this.webSocketConnection.send({
        action: 'unsubscribe',
        topics,
      });
    }
  }
}
